import React, { useEffect, useMemo, useState } from "react";
import ApiURL from "../controllers/Api";
import CompanyLayout from "./CompanyLayout";

const statusClasses = {
  pending: "bg-amber-100 text-amber-700",
  callback: "bg-sky-100 text-sky-700",
  completed: "bg-emerald-100 text-emerald-700",
  qualified: "bg-violet-100 text-violet-700",
  closed: "bg-slate-100 text-slate-700",
  not_interested: "bg-rose-100 text-rose-700",
};

const formatStatus = (status) =>
  String(status || "pending")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatDateTime = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleString();
};

export default function CompanyFollowupReport() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ total: 0, pending: 0, completed: 0, callbacks: 0 });
  const [search, setSearch] = useState("");

  useEffect(() => {
    const loadData = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${ApiURL}crm/company/get_followup_report.php`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok || result.success === false) {
          throw new Error(result.message || "Failed to load followup report");
        }
        setRows(result?.data?.rows || []);
        setSummary(result?.data?.summary || { total: 0, pending: 0, completed: 0, callbacks: 0 });
      } catch (error) {
        console.error("Error loading company followup report:", error);
      }
    };

    loadData();
  }, []);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) {
      return rows;
    }

    return rows.filter((row) =>
      [
        row.employee_name,
        row.candidate_name,
        row.candidate_role,
        row.original_filename,
        ...(row.sub_company_names || []),
        ...(row.requirement_names || []),
        row.follow_up_status,
        row.follow_up_type,
        row.notes,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term))
    );
  }, [rows, search]);

  return (
    <CompanyLayout
      title="Followup Report"
      description="Track every recruiter followup in one clean report table."
    >
      <div className="space-y-4">
        <div className="grid gap-4 md:grid-cols-4">
          <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            <div className="text-sm text-slate-500">Total Followups</div>
            <div className="mt-2 text-3xl font-bold text-slate-900">{summary.total}</div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            <div className="text-sm text-slate-500">Pending</div>
            <div className="mt-2 text-3xl font-bold text-slate-900">{summary.pending}</div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            <div className="text-sm text-slate-500">Completed</div>
            <div className="mt-2 text-3xl font-bold text-slate-900">{summary.completed}</div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            <div className="text-sm text-slate-500">Callbacks</div>
            <div className="mt-2 text-3xl font-bold text-slate-900">{summary.callbacks}</div>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white overflow-hidden shadow-sm">
          <div className="flex flex-col gap-4 border-b border-gray-200 px-4 py-4 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Recruiter Followups</h3>
              <p className="mt-1 text-sm text-slate-500">
                Review candidate followups, requirement mapping, and next-action dates.
              </p>
            </div>
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search recruiter, candidate, requirement..."
              className="w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm md:max-w-xs"
            />
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Candidate</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Recruiter</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Tagged</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Type</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Status</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Followup Date</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Next Followup</th>
                  <th className="px-3 py-3 text-left font-medium text-gray-600">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredRows.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50 align-top">
                    <td className="px-3 py-3">
                      <div className="font-medium text-slate-900">{row.candidate_name || "Candidate"}</div>
                      <div className="text-[10px] text-slate-500">{row.candidate_role || "-"}</div>
                      <div className="text-[10px] text-slate-500">{row.original_filename}</div>
                    </td>
                    <td className="px-3 py-3 text-slate-600">{row.employee_name || "-"}</td>
                    <td className="px-3 py-3 text-slate-600">
                      <div>{row.sub_company_names?.join(", ") || "-"}</div>
                      {row.requirement_names?.length ? (
                        <div className="mt-1 text-[10px] text-slate-500">{row.requirement_names.join(", ")}</div>
                      ) : null}
                    </td>
                    <td className="px-3 py-3 text-slate-600">{row.follow_up_type || "-"}</td>
                    <td className="px-3 py-3">
                      <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${statusClasses[row.follow_up_status] || "bg-slate-100 text-slate-700"}`}>
                        {formatStatus(row.follow_up_status)}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-slate-600">{formatDateTime(row.follow_up_date)}</td>
                    <td className="px-3 py-3 text-slate-600">{formatDateTime(row.next_follow_up_date)}</td>
                    <td className="px-3 py-3 text-slate-600 max-w-[220px] whitespace-pre-wrap">{row.notes || "-"}</td>
                  </tr>
                ))}
                {filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-3 py-8 text-center text-slate-500">
                      No followup records found.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </CompanyLayout>
  );
}
